import { getStudioCreditErrorBody } from 'src/utils/studioCredits';

import { ApiMethods, ApiTags, appAPI } from '.';

// Mirrors backend/lib/resources/studio_credit/web_api.go — the Studio pricing
// catalog rendered for this portal. Prices/lookup keys come from the code
// catalog; credits reflect the portal's pinned grant ladder.
export interface StudioPlanTier {
  tier: number;
  credits: number;
  monthlyCents: number;
  /** Absent for tiers not sold annually (Freemium). */
  annualCents?: number;
  monthlyLookupKey: string;
  annualLookupKey?: string;
}

export type StudioPlanId =
  | 'freemium'
  | 'starter'
  | 'professional'
  | 'advanced'
  | 'enterprise';

export interface StudioPlan {
  plan: StudioPlanId;
  tiers: StudioPlanTier[];
}

export interface StudioPlansResponse {
  catalogVersion: string;
  plans: StudioPlan[];
  /**
   * The sellable credit totals above a plan's included grant, ascending — the
   * backend's ladder, which its plan-change endpoints also validate against.
   * Read it through `useCreditLadder`; never restate the rungs client-side.
   */
  creditLadder: number[];
  /**
   * One-time packs the Add credits chooser sells. `unitCents` is the
   * per-credit price; plan discounts do not apply.
   */
  creditPacks?: CreditPackOption[];
}

export interface CreditPackOption {
  credits: number;
  unitCents: number;
}

// Mirrors backend/lib/resources/studio_credit/web_api.go CreditBalanceResponse —
// the display subset of the workspace's balance row. `creditBalance` and
// `monthlyCreditGrant` are millicredits (1000 = 1 credit); divide before
// showing them. `periodEnd` is the "credits reset on {date}" the Plans page
// renders. The same shape arrives over websocket (entity STUDIO_CREDIT_BALANCE)
// to keep the balance real time — see websocket/creditBalanceHandler.ts.
export interface CreditBalanceResponse {
  creditBalance: number;
  monthlyCreditGrant: number;
  periodStart: string;
  periodEnd: string;
  // Credit rollover, as the backend evaluated it for this workspace — the UI
  // never reads the flag itself. `expiringCredits`/`expiresAt` (millicredits +
  // RFC3339) are the next expiry among the live grants, present only when
  // something is left to expire. The websocket row carries none of these, so
  // the handler refetches to refresh them.
  rolloverEnabled?: boolean;
  expiringCredits?: number;
  expiresAt?: string;
  // Plan vs one-time piles, millicredits. Present when rollover is on, including
  // a real zero. `pausedTopUpCredits` is held back on Freemium until resubscribe.
  planCredits?: number;
  topUpCredits?: number;
  // Soonest expiry among remaining one-time packs.
  topUpExpiresAt?: string;
  // Deductions since the credit period opened, millicredits. Present when a
  // pack is still held, so "credits used" counts plan and pack spend.
  periodCreditsConsumed?: number;
  pausedTopUpCredits?: number;
  pausedTopUpExpiresAt?: string;
}

export interface CreditPackPurchaseRequest {
  credits: number;
  idempotencyKey: string;
}

export interface CreditPackPurchaseResponse {
  credits: number;
  amountCents: number;
  expiresAt: string;
}

// Mirrors backend/lib/resources/studio_credit/web_api.go CreditUsageDay — one
// UTC calendar day of the window. Days with no charges are present with zero, so
// the chart plots a continuous axis without filling gaps itself. All credit
// amounts on these types are millicredits, matching the balance row.
export interface CreditUsageDay {
  date: string;
  creditsConsumed: number;
}

// Mirrors CreditUsageApp. `name`/`icon` are display enrichment and may be
// absent when the app builder can't be resolved; `appId` is always present.
// `deleted` marks spend by an app the workspace no longer has — its name and
// icon still resolve, so it is not the same as an unresolved app.
export interface CreditUsageApp {
  appId: string;
  name?: string;
  icon?: string;
  deleted?: boolean;
  creditsConsumed: number;
}

// Mirrors CreditUsageUser. Names and avatars are resolved client-side from the
// user store rather than duplicated in the response.
export interface CreditUsageUser {
  userId: string;
  creditsConsumed: number;
}

// Mirrors CreditUsageSummaryResponse — the whole requested window aggregated
// three ways. Totals cover the entire window: the ledger is paged server-side,
// so there is no partial subtotal to mistake for a total.
export interface CreditUsageSummaryResponse {
  from: string;
  to: string;
  totalCreditsConsumed: number;
  byDay: CreditUsageDay[];
  byApp: CreditUsageApp[];
  byUser: CreditUsageUser[];
}

/**
 * Window and filters for the usage summary. `from`/`to` are RFC3339 and default
 * to the trailing 30 days; `userId`/`appId` narrow every aggregate, not just the
 * matching breakdown.
 */
export interface CreditUsageSummaryRequest {
  from?: string;
  to?: string;
  userId?: string;
  appId?: string;
}

/**
 * The wire values of {@link CurrentPlanFamily}, so a branch names the family it
 * means instead of repeating the string. A const map rather than a TS enum
 * because these arrive as plain JSON — the union stays assignable from the
 * literals the API and test fixtures already produce.
 */
export const PlanFamily = {
  studio: 'studio',
  classic: 'classic',
  none: 'none',
} as const;

export type CurrentPlanFamily = (typeof PlanFamily)[keyof typeof PlanFamily];

// Mirrors backend/lib/resources/studio_credit/web_api.go CurrentPlanResponse —
// the portal's current plan resolved from its live Stripe subscription, for
// all plan families. Studio subscriptions carry the parsed selection (plan,
// interval, credits = included + extras); Classic ones carry the accurate
// display name (fixes the empty-name heuristics for `_ent` customs and
// anonymous `price_1…` prices). `family: 'none'` means no billable
// subscription resolved (trial, cancelled, or no Stripe customer yet).

/**
 * The canonical interval strings of the Studio catalog (backend
 * plans.Interval); the Plans page's month/year toggle maps onto these before
 * hitting the plan-change routes.
 */
export type StudioPlanChangeInterval = 'monthly' | 'annual';

/**
 * The plan a pending change renews onto. Classic subscriptions schedule credit
 * decreases only — the plan itself changes immediately — so every Classic
 * pending change reports the one `classic` id rather than a catalog plan.
 */
export type PendingPlanChangePlan = StudioPlanId | 'classic';

// Mirrors backend/lib/resources/studio_credit/plan_change.go
// PendingPlanChangeInfo — a scheduled downgrade waiting for renewal.
export interface PendingPlanChange {
  plan: PendingPlanChangePlan;
  /** 'monthly' | 'annual' for Studio; the Stripe interval for Classic. */
  interval: string;
  credits: number;
  effectiveAt: string;
}

export interface ChurnAccessResponse {
  blocked: boolean;
  /** Self-serve plans that still include every paid feature in use. */
  allowedPlans?: StudioPlanId[];
  /** Unix seconds when the paid subscription ended. Omitted when unknown. */
  lastAttempt?: number;
  /** Title-cased card brand, for example Visa. Omitted when unknown. */
  paymentMethodBrand?: string;
  paymentMethodLast4?: string;
}

export interface CurrentPlanResponse {
  family: CurrentPlanFamily;
  plan?: StudioPlanId;
  /** 'monthly' | 'annual' for Studio; the Stripe interval for Classic. */
  interval?: string;
  credits?: number;
  includedCredits?: number;
  extraCredits?: number;
  planDisplayName?: string;
  renewalDate?: string;
  cancelAtPeriodEnd: boolean;
  pendingChange?: PendingPlanChange;
  /**
   * The Stripe price the plan resolves from, chosen by the backend past any
   * add-on items so the webapp never infers the plan from item order. Classic
   * IDs are the named catalog IDs the price list and CRM rules key on.
   */
  planPriceId?: string;
  /** The plan's own recurring charge in cents, excluding add-ons. Classic only. */
  baseAmountCents?: number;
  /**
   * Plan and credit changes are offered: Studio, or the Classic platform
   * catalog. Anything else (legacy, fixed-access, enterprise) is support-managed.
   */
  isSelfServe?: boolean;
  /** Negotiated pricing: no amount is quoted, contact support instead. */
  isEnterprise?: boolean;
}

// Mirrors backend/lib/resources/billing/studio/model.go AppUsageResponse — the
// workspace's app-limit state for the add-app UI. `family` follows the
// current-plan resolver: only 'studio' workspaces carry limits/fees; 'classic'
// and 'none' are exempt (`canAddPaidExtras` false, never blocked). On a paid
// Studio plan `canAddPaidExtras` is true (adding past the allowance bills the
// per-app fee); on Freemium it is false (blocked with an upgrade prompt).
export interface StudioAppUsageResponse {
  family: CurrentPlanFamily;
  includedApps: number;
  countableApps: number;
  extraApps: number;
  canAddPaidExtras: boolean;
  /** True when the plan is not app-metered at all (Enterprise). */
  unlimitedApps?: boolean;
  /** Catalog apps (the Home App) that never spend an allowance slot. */
  excludedAppIds?: string[];
}

// Mirrors backend/lib/resources/billing/studio/model.go ExtraAppPreviewResponse
// — Stripe's charge for adding one more app: `amountDueNow` is the immediate
// prorated charge and `recurringAmount` the per-app amount each interval, both
// in minor units of `currency`.
export interface ExtraAppPreviewResponse {
  amountDueNow: number;
  recurringAmount: number;
  currency: string;
  interval: string;
}

// Mirrors backend/lib/resources/billing/studio/model.go
// ExtraAppPurchaseResponse — the confirmation that the extra app is paid for.
// `alreadyPaid` means the workspace was already covered (a repeated confirm) and
// no new charge was raised.
export interface ExtraAppPurchaseResponse {
  extraApps: number;
  alreadyPaid: boolean;
}

// --- Plan-change DTOs, mirroring plan_change.go ---

export interface StudioPlanChangeRequest {
  plan: StudioPlanId;
  interval: StudioPlanChangeInterval;
  /** TOTAL monthly credits of the target selection (included + extras). */
  credits: number;
}

export interface StudioPlanChangeResponse {
  plan: StudioPlanId;
  interval: StudioPlanChangeInterval;
  credits: number;
  includedCredits: number;
  extraCredits: number;
}

export interface StudioUpgradeCheckoutRequest extends StudioPlanChangeRequest {
  successUrl: string;
  cancelUrl: string;
}

export interface StudioUpgradeCheckoutResponse {
  url: string;
}

/**
 * The downgrade reason. Every downgrade requires one written answer, so only
 * `otherReason` is sent — the endpoint's `reasons` slugs belong to the retired
 * multiple-choice questionnaire and are no longer collected.
 */
export interface StudioDowngradeSurvey {
  otherReason: string;
}

export interface StudioDowngradeRequest extends StudioPlanChangeRequest {
  survey?: StudioDowngradeSurvey;
}

export interface StudioDowngradeScheduledResponse {
  pendingChange: PendingPlanChange;
}

// Mirrors plan_change.go CreditChangeRequest — the TOTAL monthly credits of
// the target selection. Both directions hit the same route; the backend
// classifies increase vs decrease against the current subscription.
export interface StudioCreditChangeRequest {
  credits: number;
}

// Mirrors plan_change.go CreditChangeResult — an increase applies immediately
// (`credits` is the new total, `pendingChange` absent); a decrease is pinned
// to renewal (`credits` keeps the current total, `pendingChange` carries the
// scheduled one).
export interface StudioCreditChangeResponse {
  plan: StudioPlanId;
  interval: StudioPlanChangeInterval;
  credits: number;
  includedCredits: number;
  extraCredits: number;
  pendingChange?: PendingPlanChange;
}

export interface StudioDowngradeBlocker {
  code: string;
  message: string;
}

// Mirrors plan_change.go PlanChangePreviewRequest/Response — what a proposed
// change would charge today. With Plan set it previews a Studio plan upgrade;
// without, a credits-only change on the current plan.
export interface StudioPlanChangePreviewRequest {
  plan?: StudioPlanId;
  interval?: StudioPlanChangeInterval;
  credits: number;
}

export interface StudioPlanChangePreviewResponse {
  amountDueTodayCents: number;
  currency?: string;
  /** True when the change is give-less: nothing is charged today. */
  scheduledAtRenewal: boolean;
}

/**
 * Stable plan-change error codes the flows branch on, mirroring the
 * portalerr codes in plan_change.go.
 */
export const StudioPlanChangeErrorCode = {
  paymentMethodRequired: 'studio_plan_change_payment_method_required',
  useDowngradeFlow: 'studio_plan_change_use_downgrade_flow',
  useUpgradeFlow: 'studio_plan_change_use_upgrade_flow',
  noChange: 'studio_plan_change_no_change',
  downgradeBlocked: 'studio_plan_downgrade_blocked',
  noPendingDowngrade: 'studio_plan_change_no_pending_downgrade',
  /**
   * The card on file was refused when the change was billed. The response
   * message is the card network's own reason, so it is shown as-is.
   */
  cardDeclined: 'studio_plan_change_card_declined',
} as const;

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const getStudioPlanChangeErrorCode = (
  error: unknown,
): string | undefined => {
  const code = getStudioCreditErrorBody(error)?.code;
  return typeof code === 'string' ? code : undefined;
};

/**
 * The structured blocker list of a 409 blocked-downgrade rejection; undefined
 * for any other error.
 */
export const getStudioDowngradeBlockers = (
  error: unknown,
): StudioDowngradeBlocker[] | undefined => {
  if (
    getStudioPlanChangeErrorCode(error) !==
    StudioPlanChangeErrorCode.downgradeBlocked
  ) {
    return undefined;
  }
  const blockers = getStudioCreditErrorBody(error)?.blockers;
  if (!Array.isArray(blockers)) {
    return [];
  }
  return blockers.filter(
    (blocker): blocker is StudioDowngradeBlocker =>
      isObjectRecord(blocker) &&
      typeof blocker.code === 'string' &&
      typeof blocker.message === 'string',
  );
};

export const studioCreditApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getStudioPlans: build.query<StudioPlansResponse, void>({
      query: () => ({
        path: '/studio-plans',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.studioPlans],
    }),
    getCurrentPlan: build.query<CurrentPlanResponse, void>({
      query: () => ({
        path: '/current-plan',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.currentPlan],
    }),
    getChurnAccess: build.query<ChurnAccessResponse, void>({
      query: () => ({
        path: '/studio-churn-access',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.churnAccess],
    }),
    getCreditBalance: build.query<CreditBalanceResponse, void>({
      query: () => ({
        path: '/credit-balance',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.creditBalance],
    }),
    getStudioAppUsage: build.query<StudioAppUsageResponse, void>({
      query: () => ({
        path: '/studio-app-usage',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.studioAppUsage],
    }),
    // Takes the per-app fee before the app exists, so a decline stops the
    // build/add rather than surfacing as an unbilled app later. Invalidates the
    // usage read, whose extraApps this moves.
    purchaseExtraApp: build.mutation<ExtraAppPurchaseResponse, void>({
      query: () => ({
        path: '/studio-extra-apps-purchase',
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: [ApiTags.studioAppUsage],
    }),
    getExtraAppsPreview: build.query<ExtraAppPreviewResponse, void>({
      query: () => ({
        path: '/studio-extra-apps-preview',
        method: ApiMethods.get,
        options: {},
      }),
    }),
    // The usage-details screen's whole window, aggregated server-side — one
    // request per window rather than a page walk in the browser. Undefined
    // bounds and filters are dropped rather than sent empty, since the handler
    // rejects an unparseable `from`/`to` outright.
    getCreditUsageSummary: build.query<
      CreditUsageSummaryResponse,
      CreditUsageSummaryRequest
    >({
      query: ({ from, to, userId, appId }) => ({
        path: '/credit-usage-summary',
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            ...(userId ? { userId } : {}),
            ...(appId ? { appId } : {}),
          },
        },
      }),
      providesTags: [ApiTags.creditUsageSummary],
    }),
    upgradeStudioPlan: build.mutation<
      StudioPlanChangeResponse,
      StudioPlanChangeRequest
    >({
      query: (body) => ({
        path: '/studio-plan-changes/upgrade',
        method: ApiMethods.post,
        options: { body },
      }),
      // The credit delta lands via the subscription webhook, so the refetched
      // balance may briefly lag the new plan; the websocket balance push
      // catches it up.
      invalidatesTags: [
        ApiTags.currentPlan,
        ApiTags.creditBalance,
        ApiTags.churnAccess,
      ],
    }),
    setupStudioUpgradeCheckout: build.mutation<
      StudioUpgradeCheckoutResponse,
      StudioUpgradeCheckoutRequest
    >({
      query: (body) => ({
        path: '/studio-plan-changes/checkout-setup',
        method: ApiMethods.post,
        options: { body },
      }),
    }),
    downgradeStudioPlan: build.mutation<
      StudioDowngradeScheduledResponse,
      StudioDowngradeRequest
    >({
      query: (body) => ({
        path: '/studio-plan-changes/downgrade',
        method: ApiMethods.post,
        options: { body },
      }),
      invalidatesTags: [ApiTags.currentPlan],
    }),
    // Both credit-quantity directions route here (the dedicated T7 endpoint);
    // the backend applies an increase immediately and schedules a decrease at
    // renewal.
    changeStudioCredits: build.mutation<
      StudioCreditChangeResponse,
      StudioCreditChangeRequest
    >({
      query: (body) => ({
        path: '/studio-plan-changes/credits',
        method: ApiMethods.post,
        options: { body },
      }),
      // An increase grants the delta via the subscription webhook (balance); a
      // decrease writes a pending change on the current plan. Invalidate both.
      invalidatesTags: [ApiTags.currentPlan, ApiTags.creditBalance],
    }),
    purchaseCreditPack: build.mutation<
      CreditPackPurchaseResponse,
      CreditPackPurchaseRequest
    >({
      query: (body) => ({
        path: '/credit-packs',
        method: ApiMethods.post,
        options: { body },
      }),
      // The grant lands in this request. The subscription is unchanged.
      invalidatesTags: [ApiTags.creditBalance],
    }),
    cancelStudioDowngrade: build.mutation<void, void>({
      query: () => ({
        path: '/studio-plan-changes/cancel-downgrade',
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: [ApiTags.currentPlan],
    }),
    // A query despite the POST verb: previews are idempotent reads, and the
    // per-argument cache makes flipping between credit options instant.
    previewStudioPlanChange: build.query<
      StudioPlanChangePreviewResponse,
      StudioPlanChangePreviewRequest
    >({
      query: (body) => ({
        path: '/studio-plan-changes/preview',
        method: ApiMethods.post,
        options: { body },
      }),
    }),
  }),
});

export const {
  useGetStudioPlansQuery,
  useGetCreditBalanceQuery,
  useGetCreditUsageSummaryQuery,
  useGetCurrentPlanQuery,
  useGetChurnAccessQuery,
  useGetStudioAppUsageQuery,
  useGetExtraAppsPreviewQuery,
  usePurchaseExtraAppMutation,
  useUpgradeStudioPlanMutation,
  useSetupStudioUpgradeCheckoutMutation,
  useDowngradeStudioPlanMutation,
  useChangeStudioCreditsMutation,
  usePurchaseCreditPackMutation,
  useCancelStudioDowngradeMutation,
  usePreviewStudioPlanChangeQuery,
} = studioCreditApi;
